// Helpers for the "paste your own GitHub URL" flow.

const SQLITE_MAGIC = 'SQLite format 3\x00';

/**
 * Convert a github.com/.../blob/... URL into the matching
 * raw.githubusercontent.com URL so the browser can actually fetch it
 * across origins. Anything else passes through untouched.
 */
export function convertGithubUrl(input: string): string {
  const url = input.trim();
  const blob = url.match(/^https?:\/\/github\.com\/([^/]+)\/([^/]+)\/blob\/(.+)$/i);
  if (blob) {
    return `https://raw.githubusercontent.com/${blob[1]}/${blob[2]}/${blob[3]}`;
  }
  // github.com/owner/repo/raw/branch/path → raw URL too. (github.com/raw
  // redirects, but cross-origin redirects are handled inconsistently in
  // older browsers, so rewrite up-front.)
  const raw = url.match(/^https?:\/\/github\.com\/([^/]+)\/([^/]+)\/raw\/(.+)$/i);
  if (raw) {
    return `https://raw.githubusercontent.com/${raw[1]}/${raw[2]}/${raw[3]}`;
  }
  return url;
}

/**
 * Pull a sensible local DB name out of a URL: the last path segment, with
 * the SQLite extension stripped and unsafe chars replaced.
 */
export function dbNameFromUrl(url: string): string {
  try {
    const u = new URL(url);
    const last = u.pathname.split('/').filter(Boolean).pop() ?? 'imported';
    const stripped = last.replace(/\.(db|sqlite|sqlite3)$/i, '');
    return stripped.replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64) || 'imported';
  } catch {
    return 'imported';
  }
}

/**
 * Verify the first 16 bytes match SQLite's magic header so we don't OPFS-write
 * an HTML 404 page or someone's photo. Throws a clear error on mismatch.
 */
export function assertSqliteHeader(buf: ArrayBuffer): void {
  if (buf.byteLength < 16) {
    throw new Error('File is too small to be a SQLite database.');
  }
  const head = new Uint8Array(buf, 0, 16);
  for (let i = 0; i < SQLITE_MAGIC.length; i++) {
    if (head[i] !== SQLITE_MAGIC.charCodeAt(i)) {
      throw new Error(
        "That URL didn't return a SQLite database file. Double-check the link points at a .db / .sqlite file (not an HTML page).",
      );
    }
  }
}
